// Existing /app and /dashboard/app routes stay available, including
// notification setup redirects, but use the shared in-app installation UI.
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppScreenShell from "../../components/layout/AppScreenShell";
import InstallInstructions from "../../components/pwa/InstallInstructions";
import { isIOS, usePWAInstall } from "../../components/pwa/PWAInstallContext";

export default function AppInstallPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canInstall, installed, busy, install } = usePWAInstall();
  const [device, setDevice] = useState(() => (isIOS() ? "ios" : "android"));
  const [notice, setNotice] = useState("");

  const returnTo = typeof location.state?.returnTo === "string"
    ? location.state.returnTo
    : "";
  const fromNotificationSetup = location.state?.fromNotificationSetup === true;

  const startInstall = async () => {
    if (!canInstall || busy) return;
    setNotice("");
    const outcome = await install();
    if (outcome === "accepted" || outcome === "installed") {
      setNotice("Install request accepted. Look for Gwin on your Home Screen.");
    } else {
      setNotice("Use your browser's menu to finish installing Gwin.");
    }
  };

  const continueAfterInstall = () => {
    navigate(returnTo || "/register", { replace: true });
  };

  return (
    <AppScreenShell
      eyebrow="Gwin App"
      title="Add to Home Screen"
      subtitle="Install Gwin for easier access."
      maxWidth="max-w-[430px]"
      showBottomBar={false}
      contentClassName={location.pathname.startsWith("/dashboard/") ? "!pb-[120px]" : ""}
    >
      {fromNotificationSetup && (
        <p className="mb-4 rounded-[18px] border border-[#FFD8BF] bg-[#FFF4EC] p-4 text-sm font-semibold leading-relaxed text-[#6B5A4C]">
          To enable notifications on iPhone, first add Gwin to the Home Screen.
          Then open the installed app and enable notifications from Profile.
        </p>
      )}

      <div className="rounded-[24px] border border-[#E7E7E7] bg-white p-5 shadow-sm">
        {installed && (
          <p className="mb-4 rounded-xl bg-[#EDF9F1] p-3 text-sm font-bold text-green-800">
            Gwin is already running as an installed app.
          </p>
        )}
        {!installed && canInstall && (
          <button
            type="button"
            disabled={busy}
            onClick={startInstall}
            className="mb-4 min-h-[46px] w-full rounded-2xl bg-[#FF914D] px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
          >
            {busy ? "Opening installer..." : "Install Gwin"}
          </button>
        )}
        {notice && <p role="status" className="mb-4 rounded-xl bg-[#FFF4EC] p-3 text-xs font-semibold text-[#6B5A4C]">{notice}</p>}
        <div className="mb-5 grid grid-cols-2 gap-2" role="group" aria-label="Choose installation instructions">
          <button type="button" onClick={() => setDevice("android")} aria-pressed={device === "android"}
            className={`rounded-xl border px-3 py-2 text-sm font-bold ${device === "android" ? "border-[#FF914D] bg-[#FFF4EC]" : "border-[#DEDEDE]"}`}>
            Android
          </button>
          <button type="button" onClick={() => setDevice("ios")} aria-pressed={device === "ios"}
            className={`rounded-xl border px-3 py-2 text-sm font-bold ${device === "ios" ? "border-[#FF914D] bg-[#FFF4EC]" : "border-[#DEDEDE]"}`}>
            iPhone / iPad
          </button>
        </div>
        <InstallInstructions device={device} />
      </div>

      <div className="mt-5 grid gap-3">
        {fromNotificationSetup ? (
          <button type="button" onClick={continueAfterInstall}
            className="min-h-[47px] rounded-2xl bg-[#FF914D] px-5 py-3 text-sm font-black text-white">
            Continue onboarding
          </button>
        ) : (
          <>
            <button type="button" onClick={() => navigate("/register")}
              className="min-h-[47px] rounded-2xl bg-[#FF914D] px-5 py-3 text-sm font-black text-white">
              Register
            </button>
            <button type="button" onClick={() => navigate("/login")}
              className="min-h-[47px] rounded-2xl border border-[#DEDEDE] bg-white px-5 py-3 text-sm font-bold text-[#555555]">
              Log in
            </button>
          </>
        )}
        <button type="button" onClick={() => navigate(-1)} className="rounded-xl py-2 text-sm font-bold text-[#757575]">
          Back
        </button>
      </div>
    </AppScreenShell>
  );
}
